import { useState } from "react";
import { api } from "../lib/api";
import { creditos } from "../lib/formato";
import { Aviso, Campo, Modal } from "./piezas";
import CalculadoraCambio from "./CalculadoraCambio";
import { useIdioma } from "../lib/idioma";
import type { Clave } from "../idiomas/es";

/**
 * Meter dinero en la cartera del socio.
 *
 * Vive aquí y no solo en la ficha porque el momento en que hace falta es el
 * mostrador: el socio llega, elige lo que quiere, y resulta que no dejó dinero
 * en la entrada. Mandar al del turno a la ficha del socio y de vuelta significa
 * perder el carro a medias y una cola detrás.
 */

export interface SocioRecarga {
  id: string;
  nombre: string;
  apellidos: string;
  saldoCentimos: number;
}

export default function DialogoRecarga({
  socio,
  faltanCentimos,
  alCerrar,
  alHecho,
  alFallar,
}: {
  socio: SocioRecarga;
  /** Lo que le falta para lo que está comprando, si viene del mostrador. */
  faltanCentimos?: number;
  alCerrar: () => void;
  alHecho: (saldoCentimos: number) => void;
  alFallar: (mensaje: string) => void;
}) {
  const { t } = useIdioma();
  // Se propone lo justo que le falta: es lo que pide el socio nueve de cada diez veces.
  const [importe, setImporte] = useState(
    faltanCentimos && faltanCentimos > 0 ? String(faltanCentimos / 100) : "",
  );
  const [metodoPago, setMetodoPago] = useState("EFECTIVO");
  const [enviando, setEnviando] = useState(false);
  /*
   * Segunda pantalla antes de meter el dinero. Un ingreso apuntado sin haberlo
   * cobrado deja al club debiendo dinero que nunca entro, y eso no se ve hasta
   * que el socio se lo gasta.
   */
  const [confirmando, setConfirmando] = useState(false);
  const [cobrado, setCobrado] = useState(false);

  const centimos = Math.round(Number(importe.replace(",", ".")) * 100) || 0;
  const valido = centimos > 0;

  return (
    <Modal
      titulo={t("dr.titulo", { socio: `${socio.nombre} ${socio.apellidos}` })}
      alCerrar={alCerrar}
      acciones={
        <>
          <button className="boton secundario" onClick={alCerrar}>
            {t("g.cancelar")}
          </button>
          <button className="boton" disabled={!valido} onClick={() => setConfirmando(true)}>
            {t("dr.ingresar", { importe: creditos(centimos) })}
          </button>
        </>
      }
    >
      <div className="fila separada" style={{ marginBottom: 14 }}>
        <span className="suave">{t("dr.tieneAhora")}</span>
        <strong className="tabular">{creditos(socio.saldoCentimos)}</strong>
      </div>

      {faltanCentimos !== undefined && faltanCentimos > 0 && (
        <Aviso tipo="atencion">
          {t("dr.leFaltan", { importe: creditos(faltanCentimos) })}
        </Aviso>
      )}

      <div className="linea-campos">
        <Campo etiqueta={t("dr.importe")}>
          <input
            type="number"
            step="0.01"
            min={0}
            value={importe}
            onChange={(e) => setImporte(e.target.value)}
            autoFocus
          />
        </Campo>
        <Campo
          etiqueta={t("dr.formaDePago")}
          ayuda={metodoPago === "EFECTIVO" ? t("dr.entraEnCaja") : t("dr.noTocaCaja")}
        >
          <select value={metodoPago} onChange={(e) => setMetodoPago(e.target.value)}>
            {["EFECTIVO", "TARJETA", "TRANSFERENCIA"].map((m) => (
              <option key={m} value={m}>
                {t(`pago.${m}` as Clave)}
              </option>
            ))}
          </select>
        </Campo>
      </div>

      {valido && (
        <div className="fila separada pequeno" style={{ marginBottom: 10 }}>
          <span className="suave">{t("dr.seQuedaraCon")}</span>
          <strong className="tabular">{creditos(socio.saldoCentimos + centimos)}</strong>
        </div>
      )}

      {metodoPago === "EFECTIVO" && valido && <CalculadoraCambio aCobrarCentimos={centimos} />}

      <p className="suave pequeno" style={{ marginBottom: 0 }}>
        <strong>{t("dr.noEsIngreso")}</strong> {t("dr.esDelSocio")}
      </p>

      {confirmando && (
        <Modal
          titulo={t("dr.confirmarTitulo")}
          alCerrar={() => setConfirmando(false)}
          acciones={
            <>
              <button className="boton secundario" onClick={() => setConfirmando(false)}>
                {t("dr.todaviaNo")}
              </button>
              <button
                className="boton"
                disabled={enviando || !cobrado}
                onClick={async () => {
                  setEnviando(true);
                  try {
                    const mov = await api<{ saldoResultante: number }>("/cartera/recargar", {
                      metodo: "POST",
                      cuerpo: { socioId: socio.id, importeEuros: centimos / 100, metodoPago },
                    });
                    alHecho(mov.saldoResultante);
                  } catch (fallo) {
                    alFallar(fallo instanceof Error ? fallo.message : t("dr.fallo"));
                    setEnviando(false);
                    setConfirmando(false);
                  }
                }}
              >
                {enviando ? t("dr.ingresando") : t("dr.siIngresar")}
              </button>
            </>
          }
        >
          <p style={{ marginTop: 0 }}>
            {t("dr.vasASumar", {
              importe: creditos(centimos),
              socio: `${socio.nombre} ${socio.apellidos}`,
              total: creditos(socio.saldoCentimos + centimos),
            })}
          </p>
          <label className="casilla">
            <input type="checkbox" checked={cobrado} onChange={(e) => setCobrado(e.target.checked)} />
            <span>
              {metodoPago === "EFECTIVO"
                ? t("dr.confirmoEfectivo", { importe: creditos(centimos) })
                : t("dr.confirmo", { importe: creditos(centimos) })}
            </span>
          </label>
          <p className="suave pequeno" style={{ marginBottom: 0 }}>
            {t("dr.siTeEquivocas")}
          </p>
        </Modal>
      )}
    </Modal>
  );
}
